// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * AdvisorSelector Component
 *
 * Dropdown selector for support staff to choose which advisor's data to view.
 * Persists selection in sessionStorage and supports "All Advisors" option.
 */

import { useState, useEffect, useCallback } from 'react';
import { Dropdown, AppIcon, Badge } from '@loa/ui-components';
import type { DropdownOption } from '@loa/ui-components';
import { supportApi } from '../api/supportApi';
import type { AssignedAdvisorInfo } from '../types/support';

// ============================================
// Constants
// ============================================

const STORAGE_KEY = 'support-advisor-selection';

// ============================================
// Types
// ============================================

export type AdvisorSelectorProps = Readonly<{
  /** Currently selected advisor ID or 'all' */
  value: string | 'all';
  /** Callback when selection changes */
  onChange: (advisorId: string | 'all') => void;
  /** Disable the selector */
  disabled?: boolean;
  /** Show compact version (for mobile) */
  compact?: boolean;
}>;

// ============================================
// Helper Functions
// ============================================

function getTotalProfiles(advisors: AssignedAdvisorInfo[]): number {
  return advisors.reduce((sum, a) => sum + a.profileCount, 0);
}

// ============================================
// Component
// ============================================

export function AdvisorSelector({
  value,
  onChange,
  disabled = false,
  compact = false,
}: AdvisorSelectorProps) {
  const [advisors, setAdvisors] = useState<AssignedAdvisorInfo[]>([]);
  const [loading, setLoading] = useState(true);

  // ============================================
  // Data Loading
  // ============================================

  useEffect(() => {
    const loadAdvisors = async () => {
      try {
        setLoading(true);
        const data = await supportApi.getMyAssignedAdvisors();
        setAdvisors(data);

        // Restore selection from sessionStorage
        const savedSelection = sessionStorage.getItem(STORAGE_KEY);
        if (savedSelection) {
          // Validate that the saved advisor is still assigned
          const isValidSelection =
            savedSelection === 'all' ||
            data.some((a) => a.id === savedSelection);
          if (isValidSelection && savedSelection !== value) {
            onChange(savedSelection);
          }
        }
      } catch (err) {
        console.error('Failed to load assigned advisors:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAdvisors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - intentionally not including onChange/value

  // ============================================
  // Handlers
  // ============================================

  const handleChange = useCallback(
    (newValue: string) => {
      sessionStorage.setItem(STORAGE_KEY, newValue);
      onChange(newValue);
    },
    [onChange]
  );

  // ============================================
  // Options
  // ============================================

  const buildOptions = useCallback((): DropdownOption[] => {
    const totalProfiles = getTotalProfiles(advisors);

    const allOption: DropdownOption = {
      value: 'all',
      text: `All Advisors (${totalProfiles})`,
    };

    const advisorOptions: DropdownOption[] = advisors.map((advisor) => ({
      value: advisor.id,
      text: `${advisor.firstName} ${advisor.lastName} (${advisor.profileCount})`,
    }));

    return [allOption, ...advisorOptions];
  }, [advisors]);

  // ============================================
  // Render
  // ============================================

  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
        <AppIcon name="exchange" />
        <Dropdown
          search
          placeholder="Select Advisor"
          options={buildOptions()}
          value={value}
          onChange={handleChange}
          disabled={disabled || loading || advisors.length === 0}
          style={{ minWidth: '200px' }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5em',
        backgroundColor: 'rgba(0,0,0,0.05)',
        padding: '0.5em 1em',
        borderRadius: '4px',
      }}
    >
      <span style={{ fontWeight: '500', color: '#666', whiteSpace: 'nowrap' }}>
        Viewing:
      </span>
      <Dropdown
        search
        options={buildOptions()}
        value={value}
        onChange={handleChange}
        disabled={disabled || loading || advisors.length === 0}
        style={{ fontWeight: '500', color: '#333' }}
      />
      {value !== 'all' && (() => {
        const selectedAdvisor = advisors.find((a) => a.id === value);
        return selectedAdvisor && selectedAdvisor.pendingLOACount > 0 ? (
          <Badge color="orange" size="sm" style={{ marginLeft: '0.5em' }}>
            {selectedAdvisor.pendingLOACount} pending
          </Badge>
        ) : null;
      })()}
    </div>
  );
}

// ============================================
// Hook for Managing Selection State
// ============================================

export function useAdvisorSelection() {
  const [selectedAdvisorId, setSelectedAdvisorId] = useState<string | 'all'>(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved || 'all';
  });

  const handleSelectionChange = useCallback((advisorId: string | 'all') => {
    setSelectedAdvisorId(advisorId);
    sessionStorage.setItem(STORAGE_KEY, advisorId);
  }, []);

  return {
    selectedAdvisorId,
    setSelectedAdvisorId: handleSelectionChange,
  };
}

export default AdvisorSelector;
